import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useStoryGenerator } from '../hooks/useStoryGenerator';
import { AgeRange, StoryType, CharacterType, Character } from '../types/Story';
import { Button } from './common/Button';
import { useNavigate } from 'react-router-dom';
import { StoryConfig } from '../types/StoryConfig';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../hooks/useCredits';
import { PurchaseModal } from './credits/PurchaseModal';
import { CreditConfirmation } from './credits/CreditConfirmation';
import { InsufficientCreditsModal } from './credits/InsufficientCreditsModal';
import {
  Gem, Compass, WandSparkles, GraduationCap, MoonStar, HeartHandshake,
  Laugh, Ghost, Rocket, Castle, PawPrint, PersonStanding, Sparkles, Bot, ToyBrick,
} from 'lucide-react';
import { AlienIcon, DragonIcon } from './common/CustomIcons';
import { COLORS } from '../constants/colors';
import CustomInput from './common/CustomInput';

// ─── Animations ───────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(66, 99, 235, 0.15); }
  50%      { box-shadow: 0 0 0 8px rgba(66, 99, 235, 0); }
`;

// ─── Layout ───────────────────────────────────────────────────

const GeneratorContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const GeneratorHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

/*
 * The display serif the homepage headings use (loaded in index.css) —
 * this screen was the last one still shouting in the UI sans-serif.
 */
const displayFont = `'Cormorant Garamond', Georgia, 'Times New Roman', serif`;

const Title = styled.h1`
  font-family: ${displayFont};
  font-size: 2.4rem;
  font-weight: 700;
  color: ${COLORS.PRIMARY};
  line-height: 1.15;
  margin: 0 0 8px 0;
`;

const SubTitle = styled.p`
  font-size: 1.125rem;
  color: #495057;
  margin: 0;
`;

// ─── Step indicator ───────────────────────────────────────────

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 32px;
`;

const StepDot = styled.button<{ $active: boolean; $completed: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${p => p.$active ? COLORS.PRIMARY : p.$completed ? COLORS.PRIMARY : '#dee2e6'};
  background: ${p => p.$active ? COLORS.PRIMARY : p.$completed ? COLORS.PRIMARY : '#fff'};
  color: ${p => (p.$active || p.$completed) ? '#fff' : '#adb5bd'};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;

  ${p => p.$active && css`animation: ${pulseGlow} 2s ease-in-out infinite;`}

  &:hover {
    transform: scale(1.1);
  }
`;

const StepLine = styled.div<{ $completed: boolean }>`
  width: 48px;
  height: 2px;
  background: ${p => p.$completed ? COLORS.PRIMARY : '#dee2e6'};
  transition: background 0.3s ease;
`;

const StepLabel = styled.div`
  display: flex;
  justify-content: center;
  gap: 56px;
  margin-top: -4px;
  margin-bottom: 24px;
`;

const StepLabelText = styled.span<{ $active: boolean }>`
  font-size: 0.7rem;
  color: ${p => p.$active ? COLORS.PRIMARY : '#adb5bd'};
  font-weight: ${p => p.$active ? 600 : 400};
  text-align: center;
  width: 60px;
  transition: color 0.2s;
`;

// ─── Step content wrapper (animated) ──────────────────────────

const StepContent = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
`;

// ─── Chip selectors (replace dropdowns for visual options) ────

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const Chip = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 20px;

  /* Overrides the width/height attributes lucide puts on its own icons */
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  border: 1.5px solid ${p => p.$selected ? COLORS.PRIMARY : '#dee2e6'};
  background: ${p => p.$selected ? `${COLORS.PRIMARY}12` : '#fff'};
  color: ${p => p.$selected ? COLORS.PRIMARY : '#495057'};
  font-size: 0.875rem;
  font-weight: ${p => p.$selected ? 600 : 400};
  cursor: pointer;
  /* Slight overshoot on the way up — reads as a bounce rather than a slide */
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  white-space: nowrap;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    background: ${p => p.$selected ? `${COLORS.PRIMARY}18` : `${COLORS.PRIMARY}08`};
    transform: translateY(-2px) scale(1.03);
  }

  &:active {
    background: ${COLORS.HOVER_LIGHT};
    transform: translateY(0) scale(0.97);
  }
`;

const ChipLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #343a40;
`;

// ─── "Surprise us!" ───────────────────────────────────────────

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SurpriseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px dashed ${COLORS.PRIMARY};
  background: ${COLORS.PRIMARY}0d;
  color: ${COLORS.PRIMARY};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.35s ease;
  }

  &:hover {
    background: ${COLORS.PRIMARY}1a;
    border-style: solid;
    transform: translateY(-1px);
  }

  /* The die tumbles on click — the whole point of the button is the delight */
  &:hover svg {
    transform: rotate(-20deg);
  }

  &:active svg {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

/*
 * The images question used to be a solid #c4b3d8 block with a hard purple
 * border — the loudest element on the page, sitting on one of the least
 * magical questions. Same tint, a fraction of the weight.
 */
const ImagesPanel = styled.div`
  background: linear-gradient(135deg, ${COLORS.PRIMARY}12 0%, ${COLORS.PRIMARY}08 100%);
  border: 1px solid ${COLORS.PRIMARY}2e;
  border-radius: 14px;
  padding: 18px 16px;
  margin-bottom: 20px;
  text-align: center;
`;

const CenteredChipGrid = styled(ChipGrid)`
  justify-content: center;
  margin-bottom: 0;
`;

// ─── Trait suggestion chips ───────────────────────────────────

const SuggestionHint = styled.span`
  display: block;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: #6c757d;
`;

// ─── Collapsible section (for optional areas) ─────────────────

const CollapsibleHeader = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  background: ${p => p.$open ? `${COLORS.PRIMARY}0a` : '#fff'};
  border: 1px solid ${p => p.$open ? `${COLORS.PRIMARY}33` : '#e9ecef'};
  border-radius: 14px;
  cursor: pointer;
  margin-bottom: ${p => p.$open ? '16px' : '0'};
  transition: all 0.2s ease;
  font-family: ${displayFont};
  font-size: 1.2rem;
  font-weight: 700;
  color: ${p => p.$open ? COLORS.PRIMARY : '#212529'};

  &:hover {
    background: ${COLORS.PRIMARY}0a;
    border-color: ${COLORS.PRIMARY}33;
  }

  &:focus { outline: none; }

  &:active { background: ${COLORS.HOVER_LIGHT}; }

  svg {
    transition: transform 0.25s ease;
    transform: rotate(${p => p.$open ? '180deg' : '0deg'});
  }
`;

const CollapsibleBody = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$open ? '2000px' : '0'};
  opacity: ${p => p.$open ? 1 : 0};
  transition: max-height 0.35s ease, opacity 0.25s ease;
`;

// ─── Reused from original ─────────────────────────────────────

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-family: ${displayFont};
  font-size: 1.6rem;
  font-weight: 700;
  color: ${COLORS.PRIMARY};
  line-height: 1.2;
  margin: 0 0 16px 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FormColumn = styled.div<{ flex?: number }>`
  flex: ${props => props.flex || 1};
`;

const CharacterCard = styled.div`
  border: 1px solid ${COLORS.PRIMARY};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #ffffff};
`;

const CharacterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CharacterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
`;

const TraitContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const Trait = styled.div`
  background-color: #e9ecef;
  color: #495057;
  font-size: 0.875rem;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  
  button {
    background: none;
    border: none;
    color: #868e96;
    margin-left: 4px;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    
    &:hover {
      color: #fa5252;
    }
  }
  word-break: break-word;
  max-width: 100%;
`;

const TraitInput = styled.div`
  display: flex;
  gap: 8px;
`;

const AddButton = styled(Button)`
  align-self: flex-start;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: #4263eb;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const PreviewContainer = styled.div`
  margin-top: 32px;
  padding: 24px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const PreviewTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 16px 0;
  text-align: center;
`;

const PreviewContent = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #212529;
  margin-bottom: 24px;
  text-align: justify;
  
  p {
    margin-bottom: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const ErrorMessage = styled.div`
  color: #fa5252;
  background-color: #fff5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const InfoMessage = styled.div`
  color: ${COLORS.PRIMARY};
  background-color: ${COLORS.BLOG_PRIMARY_BACKGROUND};
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.813rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-left: 3px solid ${COLORS.PRIMARY};
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ChipSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
`;

const ResizableTextarea = styled.textarea`
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  color: #212529;
  resize: vertical;
  line-height: 1.5;

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Ccircle cx='3.5' cy='3.5' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='7.5' cy='3.5' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='3.5' cy='7.5' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='7.5' cy='7.5' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='11' cy='7.5' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='7.5' cy='11' r='1' fill='%23adb5bd'/%3E%3Ccircle cx='11' cy='11' r='1' fill='%23adb5bd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: bottom 4px right 4px;

  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 0 0 2px ${COLORS.PRIMARY}20;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

// ─── Visibility toggle ───────────────────────────────────────

const VisibilityToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  margin-bottom: 16px;
`;

const ToggleSwitch = styled.button<{ $active: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${p => p.$active ? COLORS.PRIMARY : '#dee2e6'};
  cursor: pointer;
  transition: background 0.25s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.$active ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    transition: left 0.25s ease;
  }
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 15px;
    height: 15px;
    color: #868e96;
  }
`;

const ToggleDescription = styled.span`
  font-size: 0.75rem;
  color: #868e96;
`;

// ─── Navigation buttons row ──────────────────────────────────

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

// ─── Chevron icon helper ──────────────────────────────────────

const ChevronDown = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

/*
 * Pictures for the two taxonomies a child has to choose from. "Mythical
 * Being" and "Science Fiction" are adult words — an icon lets a child who
 * can't read yet point at the one they want.
 *
 * lucide covers everything except aliens and dragons, which come from
 * common/CustomIcons (the dragon is the same drawing as the homepage hero).
 */
const STORY_TYPE_ICONS: Record<string, React.ComponentType<any>> = {
  [StoryType.ADVENTURE]: Compass,
  [StoryType.FANTASY]: WandSparkles,
  [StoryType.EDUCATIONAL]: GraduationCap,
  [StoryType.BEDTIME]: MoonStar,
  [StoryType.MORAL]: HeartHandshake,
  [StoryType.FUNNY]: Laugh,
  [StoryType.SCARY]: Ghost,
  [StoryType.SCIENCE_FICTION]: Rocket,
  [StoryType.HISTORICAL]: Castle,
  [StoryType.ANIMAL]: PawPrint,
};

const CHARACTER_TYPE_ICONS: Record<string, React.ComponentType<any>> = {
  [CharacterType.HUMAN]: PersonStanding,
  [CharacterType.ANIMAL]: PawPrint,
  [CharacterType.MAGICAL]: Sparkles,
  [CharacterType.ROBOT]: Bot,
  [CharacterType.ALIEN]: AlienIcon,
  [CharacterType.MYTHICAL]: DragonIcon,
  [CharacterType.OBJECT]: ToyBrick,
};

/*
 * Traits offered as one-tap chips. A child can point at these; typing a
 * sentence into a textarea is something only the parent can do.
 */
const TRAIT_SUGGESTION_KEYS = [
  'brave', 'silly', 'kind', 'shy', 'clever', 'bouncy',
  'grumpy', 'sleepy', 'curious', 'forgetful', 'gentle', 'bossy',
] as const;

/*
 * Appearance is a separate row because it does a different job: these are
 * the words the image generator actually has something to draw from. A
 * character who is only "brave and silly" gives it nothing to work with.
 * Chosen to fit humans, animals and magical creatures alike, since the
 * hero type varies.
 */
const APPEARANCE_SUGGESTION_KEYS = [
  'tiny', 'enormous', 'curlyHair', 'longHair', 'freckles',
  'stripey', 'spotty', 'fluffy', 'sparkly', 'wings', 'longTail',
] as const;

/*
 * A name alone tells the image model nothing about gender, so a "Maria"
 * can come back drawn as a boy. This goes into traits like everything
 * else, which is all the prompt builder needs. Mutually exclusive, and
 * deselectable — leaving it blank is a valid answer.
 */
const GENDER_TRAIT_KEYS = ['boy', 'girl'] as const;

/*
 * Names for "Surprise us!". Kept in code rather than the locale files
 * because these are picked to read naturally in all six languages —
 * translating a proper noun would only make them less neutral.
 */
const SURPRISE_NAMES = ['Luna', 'Milo', 'Nina', 'Tobi', 'Mira', 'Emil', 'Sasha', 'Nico'];

const randomOf = <T,>(items: readonly T[]): T =>
  items[Math.floor(Math.random() * items.length)];

/*
 * The two chip rows, rendered identically for the main character and for
 * each supporting one. Both rows toggle into the same traits list — the
 * split is for the parent's benefit, not the backend's.
 */
interface TraitChipRow {
  hint: string;
  items: { key: string; label: string }[];
  /* Picking one clears the others in the row — used for gender */
  exclusive?: boolean;
}

const TraitChipRows: React.FC<{
  rows: TraitChipRow[];
  selected: string[];
  onToggle: (label: string, exclusiveGroup?: string[]) => void;
}> = ({ rows, selected, onToggle }) => (
  <>
    {rows.map(row => {
      const group = row.exclusive ? row.items.map(i => i.label) : undefined;
      return (
        <React.Fragment key={row.hint}>
          <SuggestionHint>{row.hint}</SuggestionHint>
          <ChipGrid>
            {row.items.map(item => (
              <Chip
                key={item.key}
                $selected={selected.includes(item.label)}
                onClick={() => onToggle(item.label, group)}
              >
                {item.label}
              </Chip>
            ))}
          </ChipGrid>
        </React.Fragment>
      );
    })}
  </>
);

/* Five-pip die */
const DiceIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M8 8h.01M16 8h.01M8 16h.01M16 16h.01M12 12h.01" />
  </svg>
);

export const StoryGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const { credits, refreshCredits } = useCredits();
  
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [finalConfigForGeneration, setFinalConfigForGeneration] = useState<StoryConfig | null>(null);

  const { 
    loading, 
    error, 
    generatedStory, 
    progress, 
    generateNewStory, 
    saveStory, 
    resetGenerator 
  } = useStoryGenerator();
  
  // ─── Wizard step ───────────────────────────────────────────
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 3; // 0: Story basics, 1: Main character, 2: Extras & generate

  /*
   * Each step is taller than the viewport, so advancing leaves the user
   * part-way down the next one — easy to hit Next again without ever
   * seeing the questions. Jump back to the top on every step change,
   * whether it came from Next, Back, or a step dot.
   */
  const generatorRef = useRef<HTMLDivElement>(null);
  const skipFirstScroll = useRef(true);

  useEffect(() => {
    if (skipFirstScroll.current) {
      skipFirstScroll.current = false;
      return;
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    generatorRef.current?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [step]);
  
  // ─── Collapsible sections ──────────────────────────────────
  const [showSupportingChars, setShowSupportingChars] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  
  // ─── Story visibility ──────────────────────────────────────
  const [storyVisibility, setStoryVisibility] = useState<'public' | 'private'>('public');
  
  // Story configuration state
  const [config, setConfig] = useState<StoryConfig>({
    childAge: AgeRange.PRESCHOOL,
    childGender: 'neutral',
    storyType: StoryType.ADVENTURE,
    mainCharacter: {
      id: '1',
      name: '',
      type: CharacterType.HUMAN,
      traits: [],
    },
    supportingCharacters: [],
    lengthPreference: 'medium',
    includeMagic: false,
    language: i18n.language,
    generateImages: true,
  });

  useEffect(() => {
    setConfig(prev => ({ ...prev, language: i18n.language }));
  }, [i18n.language]);

  // Ensure generateImages defaults to true on every mount/remount
  useEffect(() => {
    setConfig(prev => ({ ...prev, generateImages: true }));
  }, []);
  
  // Form state
  const [newTrait, setNewTrait] = useState<string>('');
  const [supportingCharName, setSupportingCharName] = useState<string>('');
  const [supportingCharType, setSupportingCharType] = useState<CharacterType>(CharacterType.ANIMAL);
  const [supportingCharTrait, setSupportingCharTrait] = useState<string>('');
  const [supportingCharTraits, setSupportingCharTraits] = useState<string[]>([]);
  
  // ─── Handlers (unchanged logic) ────────────────────────────

  const handleConfigChange = <K extends keyof StoryConfig>(key: K, value: StoryConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };
  
  const handleMainCharNameChange = (name: string) => {
    setConfig(prev => ({
      ...prev,
      mainCharacter: { ...prev.mainCharacter, name },
    }));
  };
  
  /*
   * The gender row only exists for humans, so its answer is dropped when
   * the character stops being one — otherwise "a girl" would keep reaching
   * the image prompt from a row the user can no longer see.
   */
  const handleMainCharTypeChange = (type: CharacterType) => {
    setConfig(prev => ({
      ...prev,
      mainCharacter: {
        ...prev.mainCharacter,
        type,
        traits: type === CharacterType.HUMAN
          ? prev.mainCharacter.traits
          : prev.mainCharacter.traits.filter(tr => !genderLabels.includes(tr)),
      },
    }));
  };

  const handleSupportingCharTypeChange = (type: CharacterType) => {
    setSupportingCharType(type);
    if (type !== CharacterType.HUMAN) {
      setSupportingCharTraits(prev => prev.filter(tr => !genderLabels.includes(tr)));
    }
  };
  
  const handleAddTrait = () => {
    if (!newTrait.trim()) return;
    setConfig(prev => ({
      ...prev,
      mainCharacter: {
        ...prev.mainCharacter,
        traits: [...prev.mainCharacter.traits, newTrait.trim()],
      },
    }));
    setNewTrait('');
  };
  
  const handleRemoveTrait = (index: number) => {
    setConfig(prev => ({
      ...prev,
      mainCharacter: {
        ...prev.mainCharacter,
        traits: prev.mainCharacter.traits.filter((_, i) => i !== index),
      },
    }));
  };

  // ─── Trait suggestion chips ────────────────────────────────
  /*
   * The chips store their translated label, so a story generated in
   * Romanian gets Romanian traits. Selection state is therefore just
   * "is this label already in the list".
   */
  const traitSuggestions = TRAIT_SUGGESTION_KEYS.map(key => ({
    key,
    label: t(`storyGenerator.form.characterTraits.suggestions.${key}`, key),
  }));

  const appearanceSuggestions = APPEARANCE_SUGGESTION_KEYS.map(key => ({
    key,
    label: t(`storyGenerator.form.characterTraits.appearance.${key}`, key),
  }));

  const genderSuggestions = GENDER_TRAIT_KEYS.map(key => ({
    key,
    label: t(`storyGenerator.form.characterTraits.gender.${key}`, key),
  }));

  const genderLabels = genderSuggestions.map(s => s.label);

  /*
   * Gender leads, because it is the answer the illustrations most often
   * get wrong. Human characters only — that is where a name alone leaves
   * the image model guessing.
   */
  const buildTraitRows = (type: CharacterType): TraitChipRow[] => [
    ...(type === CharacterType.HUMAN
      ? [{
          hint: t('storyGenerator.form.characterTraits.hintGender', 'Boy or girl? — so the pictures match'),
          items: genderSuggestions,
          exclusive: true,
        }]
      : []),
    {
      hint: t('storyGenerator.form.characterTraits.hintPersonality', "What they're like"),
      items: traitSuggestions,
    },
    {
      hint: t('storyGenerator.form.characterTraits.hintAppearance', 'What they look like — these shape the pictures'),
      items: appearanceSuggestions,
    },
  ];

  /*
   * Selecting inside an exclusive row (gender) drops the row's other
   * options, so a character can never be both a boy and a girl.
   */
  const applyTraitToggle = (traits: string[], label: string, exclusiveGroup?: string[]) => {
    if (traits.includes(label)) return traits.filter(tr => tr !== label);
    const cleared = exclusiveGroup ? traits.filter(tr => !exclusiveGroup.includes(tr)) : traits;
    return [...cleared, label];
  };

  const toggleMainCharTrait = (label: string, exclusiveGroup?: string[]) => {
    setConfig(prev => ({
      ...prev,
      mainCharacter: {
        ...prev.mainCharacter,
        traits: applyTraitToggle(prev.mainCharacter.traits, label, exclusiveGroup),
      },
    }));
  };

  const toggleSupportingCharTrait = (label: string, exclusiveGroup?: string[]) => {
    setSupportingCharTraits(prev => applyTraitToggle(prev, label, exclusiveGroup));
  };

  // ─── "Surprise us!" ────────────────────────────────────────
  /*
   * Only ever randomises creative choices. The child's own age, name and
   * gender are real details about a real child, never dice rolls.
   */
  const handleSurpriseStep0 = () => {
    setConfig(prev => ({
      ...prev,
      storyType: randomOf(storyTypeOptions).value as StoryType,
      lengthPreference: randomOf(lengthOptions).value as 'short' | 'medium' | 'long',
    }));
  };

  const handleSurpriseStep1 = () => {
    // Two of each, so a rolled character always has something to draw.
    const pickTwo = (items: { label: string }[]) =>
      [...items].sort(() => Math.random() - 0.5).slice(0, 2).map(s => s.label);

    const type = randomOf(characterTypeOptions).value as CharacterType;
    const picked = [
      // Matches the chip row: humans get a gender so the illustrations
      // aren't left guessing from the name alone.
      ...(type === CharacterType.HUMAN ? [randomOf(genderSuggestions).label] : []),
      ...pickTwo(traitSuggestions),
      ...pickTwo(appearanceSuggestions),
    ];

    setConfig(prev => ({
      ...prev,
      mainCharacter: {
        ...prev.mainCharacter,
        name: prev.mainCharacter.name.trim() || randomOf(SURPRISE_NAMES),
        type,
        traits: picked,
      },
    }));
  };

  
  const handleAddSupportingCharTrait = () => {
    if (!supportingCharTrait.trim()) return;
    setSupportingCharTraits(prev => [...prev, supportingCharTrait.trim()]);
    setSupportingCharTrait('');
  };
  
  const handleRemoveSupportingCharTrait = (index: number) => {
    setSupportingCharTraits(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleAddSupportingCharacter = () => {
    if (!supportingCharName.trim()) return;
    
    let finalTraits = supportingCharTraits;
    if (supportingCharTrait.trim()) {
      finalTraits = [...supportingCharTraits, supportingCharTrait.trim()];
    }
    
    const newCharacter: Character = {
      id: Date.now().toString(),
      name: supportingCharName.trim(),
      type: supportingCharType,
      traits: finalTraits,
    };
    
    setConfig(prev => ({
      ...prev,
      supportingCharacters: [...prev.supportingCharacters, newCharacter],
    }));
    
    setSupportingCharName('');
    setSupportingCharType(CharacterType.ANIMAL);
    setSupportingCharTraits([]);
    setSupportingCharTrait('');
  };
  
  const handleRemoveSupportingCharacter = (id: string) => {
    setConfig(prev => ({
      ...prev,
      supportingCharacters: prev.supportingCharacters.filter(char => char.id !== id),
    }));
  };
  
  const handleGenerateStory = async () => {
    setGenerationError(null);
    
    let updatedConfig = { ...config };
    
    if (newTrait.trim()) {
      updatedConfig = {
        ...updatedConfig,
        mainCharacter: {
          ...updatedConfig.mainCharacter,
          traits: [...updatedConfig.mainCharacter.traits, newTrait.trim()]
        }
      };
      setNewTrait('');
    }
    
    if (supportingCharName.trim()) {
      let finalTraits = supportingCharTraits;
      if (supportingCharTrait.trim()) {
        finalTraits = [...supportingCharTraits, supportingCharTrait.trim()];
      }
      
      const pendingCharacter: Character = {
        id: Date.now().toString(),
        name: supportingCharName.trim(),
        type: supportingCharType,
        traits: finalTraits,
      };
      
      updatedConfig = {
        ...updatedConfig,
        supportingCharacters: [...updatedConfig.supportingCharacters, pendingCharacter],
      };
      
      setSupportingCharName('');
      setSupportingCharType(CharacterType.ANIMAL);
      setSupportingCharTraits([]);
      setSupportingCharTrait('');
    }
    
    setFinalConfigForGeneration(updatedConfig);
    
    if (updatedConfig !== config) {
      setConfig(updatedConfig);
    }
    
    if (!updatedConfig.mainCharacter.name) {
      alert('Please provide a name for the main character');
      return;
    }
    
    if (credits < creditCost) {
      setShowInsufficientCredits(true);
      return;
    }
    
    setShowCreditConfirmation(true);
  };

  const handleConfirmedGenerate = async () => {
    setIsGeneratingStory(true);

    try {
      const configToUse = finalConfigForGeneration || config;
      await generateNewStory(configToUse);
      setShowCreditConfirmation(false);
      setIsGeneratingStory(false);
      setFinalConfigForGeneration(null);
      await refreshCredits();
    } catch (err: any) {
      console.error('Story generation error:', err);
      setIsGeneratingStory(false);

      if (err.response?.status === 400 &&
        err.response?.data?.error?.includes('Insufficient credits')) {
        setShowCreditConfirmation(false);
        setShowInsufficientCredits(true);
        setFinalConfigForGeneration(null);
        await refreshCredits();
      } else {
        setGenerationError(
          err.response?.data?.error ||
          err.message ||
          t('story.generationFailedMessage', 'Story generation failed. Please try again.')
        );
      }
    }
  };

  const handlePurchaseClick = () => {
    setShowInsufficientCredits(false);
    setShowPurchaseModal(true);
  };
  
  const handleSaveStory = async () => {
    const storyId = await saveStory(storyVisibility);
    if (storyId) {
      navigate(`/story/${storyId}/read`);
    }
  };
  
  // ─── Options ────────────────────────────────────────────────

  const ageRangeOptions = [
    { value: AgeRange.TODDLER, label: t('storyGenerator.form.childAge.options.toddler') },
    { value: AgeRange.PRESCHOOL, label: t('storyGenerator.form.childAge.options.preschool') },
    { value: AgeRange.SCHOOL_AGE, label: t('storyGenerator.form.childAge.options.schoolAge') },
    { value: AgeRange.PRETEEN, label: t('storyGenerator.form.childAge.options.preteen') },
  ];
  
  const genderOptions = [
    { value: 'boy', label: t('storyGenerator.form.childGender.options.boy') },
    { value: 'girl', label: t('storyGenerator.form.childGender.options.girl') },
    { value: 'neutral', label: t('storyGenerator.form.childGender.options.neutral') },
  ];
  
  const storyTypeOptions = [
    { value: StoryType.ADVENTURE, label: t('storyGenerator.form.storyType.options.adventure') },
    { value: StoryType.FANTASY, label: t('storyGenerator.form.storyType.options.fantasy') },
    { value: StoryType.EDUCATIONAL, label: t('storyGenerator.form.storyType.options.educational') },
    { value: StoryType.BEDTIME, label: t('storyGenerator.form.storyType.options.bedtime') },
    { value: StoryType.MORAL, label: t('storyGenerator.form.storyType.options.moral') },
    { value: StoryType.FUNNY, label: t('storyGenerator.form.storyType.options.funny') },
    { value: StoryType.SCARY, label: t('storyGenerator.form.storyType.options.scary') },
    { value: StoryType.SCIENCE_FICTION, label: t('storyGenerator.form.storyType.options.scienceFiction') },
    { value: StoryType.HISTORICAL, label: t('storyGenerator.form.storyType.options.historical') },
    { value: StoryType.ANIMAL, label: t('storyGenerator.form.storyType.options.animal') },
  ];
  
  const characterTypeOptions = [
    { value: CharacterType.HUMAN, label: t('storyGenerator.form.characterType.options.human') },
    { value: CharacterType.ANIMAL, label: t('storyGenerator.form.characterType.options.animal') },
    { value: CharacterType.MAGICAL, label: t('storyGenerator.form.characterType.options.magical') },
    { value: CharacterType.ROBOT, label: t('storyGenerator.form.characterType.options.robot') },
    { value: CharacterType.ALIEN, label: t('storyGenerator.form.characterType.options.alien') },
    { value: CharacterType.MYTHICAL, label: t('storyGenerator.form.characterType.options.mythical') },
    { value: CharacterType.OBJECT, label: t('storyGenerator.form.characterType.options.object') },
  ];
  
  const lengthOptions = [
    { value: 'short', label: t('storyGenerator.form.storyLength.options.short') },
    { value: 'medium', label: t('storyGenerator.form.storyLength.options.medium') },
    { value: 'long', label: t('storyGenerator.form.storyLength.options.long') },
  ];
  
  const isGenerateDisabled = 
    loading || 
    !config.mainCharacter.name ||
    config.mainCharacter.name.trim() === '';

  const creditCost = config.generateImages ? 4 : 2;

  // ─── Step labels for indicator ─────────────────────────────
  /*
   * Deliberately separate from the section headings: the indicator only has
   * room for two or three words, while the headings are full questions.
   */
  const stepLabels = [
    t('storyGenerator.steps.kind', 'What kind?'),
    t('storyGenerator.steps.who', "Who's in it?"),
    t('storyGenerator.steps.extras', 'Anything else?'),
  ];

  // ═══════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════
  
  return (
    <>
      <GeneratorContainer ref={generatorRef}>
        <GeneratorHeader>
          {/* Once the story exists, the header stops asking for input */}
          <Title>
            {generatedStory
              ? t('storyGenerator.header.titleDone', 'Your story is ready!')
              : t('storyGenerator.header.title')}
          </Title>
          <SubTitle>
            {generatedStory
              ? t('storyGenerator.header.subtitleDone', 'Read it together — then save it below, or start another one.')
              : t('storyGenerator.header.subtitle')}
          </SubTitle>
          <div style={{
            marginTop: '12px',
            fontSize: '14px',
            color: '#6c757d'
          }}>
            {t('credits.available')}: <strong style={{ color: COLORS.PRIMARY }}>
              <Gem style={{ marginRight: '4px' }} aria-hidden="true" />
              {credits}
            </strong>
          </div>
        </GeneratorHeader>
      
        {loading && (
          <>
            <ProgressBar>
              <ProgressFill progress={progress} />
            </ProgressBar>
            <p style={{ textAlign: 'center', color: '#495057' }}>
              {progress < 50 
                ? t('storyGenerator.progress.crafting')
                : progress < 90
                  ? t('storyGenerator.progress.addingDetails')
                  : t('storyGenerator.progress.almostDone') }
            </p>
          </>
        )}
      
        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}
      
        {generatedStory ? (
          <PreviewContainer>
            <PreviewTitle>{generatedStory.story.title}</PreviewTitle>

            {(() => {
              const paragraphs = generatedStory.story.content.split('\n\n');
              const chunkSize = Math.ceil(paragraphs.length / 2);
              const chunks = [
                paragraphs.slice(0, chunkSize),
                paragraphs.slice(chunkSize)
              ];

              return (
                <>
                  {generatedStory.imageUrls?.[0] && (
                    <img
                      src={generatedStory.imageUrls[0]}
                      alt="Story illustration number 1"
                      style={{ width: '100%', marginBottom: '16px', borderRadius: '8px' }}
                    />
                  )}
                  <PreviewContent>
                    {chunks[0].map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </PreviewContent>
                  {generatedStory.imageUrls?.[1] && (
                    <img
                      src={generatedStory.imageUrls[1]}
                      alt="Story illustration number 2"
                      style={{ width: '100%', marginBottom: '16px', borderRadius: '8px' }}
                    />
                  )}
                  <PreviewContent>
                    {chunks[1].map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </PreviewContent>
                  {generatedStory.imageUrls?.[2] && (
                    <img
                      src={generatedStory.imageUrls[2]}
                      alt="Story illustration number 3"
                      style={{ width: '100%', marginBottom: '16px', borderRadius: '8px' }}
                    />
                  )}
                </>
              );
            })()}

            <VisibilityToggle>
              <ToggleSwitch 
                $active={storyVisibility === 'public'} 
                onClick={() => setStoryVisibility(v => v === 'public' ? 'private' : 'public')}
                aria-label={t('storyGenerator.visibility.toggle', 'Toggle story visibility')}
              />
              <ToggleLabel>
                <ToggleTitle>
                  {storyVisibility === 'public' ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      {t('storyGenerator.visibility.public', 'Public')}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      {t('storyGenerator.visibility.private', 'Private')}
                    </>
                  )}
                </ToggleTitle>
                <ToggleDescription>
                  {storyVisibility === 'public' 
                    ? t('storyGenerator.visibility.publicDesc', 'Everyone can discover and read this story')
                    : t('storyGenerator.visibility.privateDesc', 'Only you can see this story')
                  }
                </ToggleDescription>
              </ToggleLabel>
            </VisibilityToggle>

            <ButtonContainer>
              <Button variant="outline" onClick={resetGenerator}>
                {t('storyGenerator.buttons.createAnother')}
              </Button>
              <Button variant="primary" onClick={handleSaveStory} isLoading={loading}>
                {t('storyGenerator.buttons.saveStory')}
              </Button>
            </ButtonContainer>
          </PreviewContainer>

        ) : (
          <>
            {/* ── Step indicator ── */}
            <StepIndicator>
              {stepLabels.map((_, i) => (
                <React.Fragment key={i}>
                  <StepDot
                    $active={step === i}
                    $completed={step > i}
                    onClick={() => setStep(i)}
                    aria-label={`Step ${i + 1}`}
                  >
                    {step > i ? <CheckIcon /> : i + 1}
                  </StepDot>
                  {i < TOTAL_STEPS - 1 && <StepLine $completed={step > i} />}
                </React.Fragment>
              ))}
            </StepIndicator>
            <StepLabel>
              {stepLabels.map((label, i) => (
                <StepLabelText key={i} $active={step === i}>{label}</StepLabelText>
              ))}
            </StepLabel>

            {/* ════════════════════════════════════════════════ */}
            {/* STEP 0 — Story basics                          */}
            {/* ════════════════════════════════════════════════ */}
            {step === 0 && (
              <StepContent key="step-0">
                <SectionHeaderRow>
                  <SectionTitle style={{ margin: 0 }}>
                    {t('storyGenerator.sections.storySettings')}
                  </SectionTitle>
                  <SurpriseButton onClick={handleSurpriseStep0}>
                    <DiceIcon />
                    {t('storyGenerator.buttons.surpriseUs', 'Surprise us!')}
                  </SurpriseButton>
                </SectionHeaderRow>

                {/* Story type as visual chips */}
                <ChipSection>
                <ChipLabel>{t('storyGenerator.form.storyType.label')}</ChipLabel>
                <ChipGrid>
                  {storyTypeOptions.map(opt => {
                    const Icon = STORY_TYPE_ICONS[opt.value];
                    return (
                      <Chip
                        key={opt.value}
                        $selected={config.storyType === opt.value}
                        onClick={() => handleConfigChange('storyType', opt.value as StoryType)}
                      >
                        {Icon && <Icon />}
                        {opt.label}
                      </Chip>
                    );
                  })}
                </ChipGrid>
                </ChipSection>

                {/* Age range as chips */}
                <ChipSection>
                <ChipLabel>{t('storyGenerator.form.childAge.label')}</ChipLabel>
                <ChipGrid>
                  {ageRangeOptions.map(opt => (
                    <Chip
                      key={opt.value}
                      $selected={config.childAge === opt.value}
                      onClick={() => handleConfigChange('childAge', opt.value as AgeRange)}
                    >
                      {opt.label}
                    </Chip>
                  ))}
                </ChipGrid>
                </ChipSection>

                {/* Length as chips */}
                <ChipSection>
                <ChipLabel>{t('storyGenerator.form.storyLength.label')}</ChipLabel>
                <ChipGrid>
                  {lengthOptions.map(opt => (
                    <Chip
                      key={opt.value}
                      $selected={config.lengthPreference === opt.value}
                      onClick={() => handleConfigChange('lengthPreference', opt.value as 'short' | 'medium' | 'long')}
                    >
                      {opt.label}
                    </Chip>
                  ))}
                </ChipGrid>
                </ChipSection>

                {/* Gender as chips */}
                <ChipSection>
                <ChipLabel>{t('storyGenerator.form.childGender.label')}</ChipLabel>
                <ChipGrid>
                  {genderOptions.map(opt => (
                    <Chip
                      key={opt.value}
                      $selected={(config.childGender || 'neutral') === opt.value}
                      onClick={() => handleConfigChange('childGender', opt.value as 'boy' | 'girl' | 'neutral')}
                    >
                      {opt.label}
                    </Chip>
                  ))}
                </ChipGrid>
                </ChipSection>

                <FormRow>
                  <FormColumn>
                    <CustomInput
                      label={t('storyGenerator.form.childName.label')}
                      placeholder={t('storyGenerator.form.childName.placeholder')}
                      value={config.childName || ''}
                      onChange={(e) => handleConfigChange('childName', e.target.value)}
                    />
                  </FormColumn>
                </FormRow>

                <NavRow>
                  <div />
                  <Button variant="primary" onClick={() => setStep(1)}>
                    {t('storyGenerator.buttons.next', 'Next')} →
                  </Button>
                </NavRow>
              </StepContent>
            )}

            {/* ════════════════════════════════════════════════ */}
            {/* STEP 1 — Main character                        */}
            {/* ════════════════════════════════════════════════ */}
            {step === 1 && (
              <StepContent key="step-1">
                <SectionHeaderRow>
                  <SectionTitle style={{ margin: 0 }}>
                    {t('storyGenerator.sections.mainCharacter')}
                  </SectionTitle>
                  <SurpriseButton onClick={handleSurpriseStep1}>
                    <DiceIcon />
                    {t('storyGenerator.buttons.surpriseUs', 'Surprise us!')}
                  </SurpriseButton>
                </SectionHeaderRow>

                <ChipSection>
                <FormRow>
                  <FormColumn flex={2}>
                    <CustomInput
                      label={config.mainCharacter.type === CharacterType.OBJECT
                        ? t('storyGenerator.form.objectName.label', 'Object name')
                        : t('storyGenerator.form.characterName.label')}
                      placeholder={config.mainCharacter.type === CharacterType.OBJECT
                        ? t('storyGenerator.form.objectName.placeholder', 'e.g. a magical oak tree, a talking flower')
                        : t('storyGenerator.form.characterName.placeholder')}
                      value={config.mainCharacter.name}
                      onChange={(e) => handleMainCharNameChange(e.target.value)}
                      isError={!config.mainCharacter.name.trim()}
                      required={true}
                    />
                  </FormColumn>
                </FormRow>

                {/* Character type as chips */}
                <ChipLabel>{t('storyGenerator.form.characterType.label')}</ChipLabel>
                <ChipGrid>
                  {characterTypeOptions.map(opt => {
                    const Icon = CHARACTER_TYPE_ICONS[opt.value];
                    return (
                      <Chip
                        key={opt.value}
                        $selected={config.mainCharacter.type === opt.value}
                        onClick={() => handleMainCharTypeChange(opt.value as CharacterType)}
                      >
                        {Icon && <Icon />}
                        {opt.label}
                      </Chip>
                    );
                  })}
                </ChipGrid>

                {/* Traits */}
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 500, color: '#343a40' }}>
                  {t('storyGenerator.form.characterTraits.label')}
                </label>
                
                <InfoMessage>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                  <span>{t('storyGenerator.form.characterTraits.infoMessage')}</span>
                </InfoMessage>

                {/* One-tap traits, so the child can choose by pointing */}
                <TraitChipRows
                  rows={buildTraitRows(config.mainCharacter.type)}
                  selected={config.mainCharacter.traits}
                  onToggle={toggleMainCharTrait}
                />

                <TraitContainer>
                  {config.mainCharacter.traits.map((trait, index) => (
                    <Trait key={index}>
                      {trait}
                      <button onClick={() => handleRemoveTrait(index)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </Trait>
                  ))}
                </TraitContainer>
                
                <TraitInput>
                  {/* <CustomInput
                    placeholder={t('storyGenerator.form.characterTraits.placeholder')}
                    value={newTrait}
                    onChange={(e) => setNewTrait(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTrait()}
                  /> */}
                      <ResizableTextarea
                        placeholder={t('storyGenerator.form.characterTraits.placeholder')}
                        value={newTrait}
                        onChange={(e) => setNewTrait(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleAddTrait();
                          }
                        }}
                        rows={2}
                      />
                  <Button variant="secondary" onClick={handleAddTrait} disabled={!newTrait.trim()}>
                    {t('storyGenerator.buttons.add')}
                  </Button>
                </TraitInput>
                </ChipSection>

                <NavRow>
                  <Button variant="outline" onClick={() => setStep(0)}>
                    ← {t('storyGenerator.buttons.back', 'Back')}
                  </Button>
                  <Button variant="primary" onClick={() => setStep(2)}>
                    {t('storyGenerator.buttons.next', 'Next')} →
                  </Button>
                </NavRow>
              </StepContent>
            )}

            {/* ════════════════════════════════════════════════ */}
            {/* STEP 2 — Extras & generate                     */}
            {/* ════════════════════════════════════════════════ */}
            {step === 2 && (
              <StepContent key="step-2">

                {/*
                  Promoted out of the collapsed panel: these are the two
                  questions a child actually enjoys answering, and they were
                  hidden behind a fold nobody opens.
                */}
                <FormRow>
                  <FormColumn>
                    <CustomInput
                      label={t('storyGenerator.form.setting.label')}
                      placeholder={t('storyGenerator.form.setting.placeholder')}
                      value={config.setting || ''}
                      onChange={(e) => handleConfigChange('setting', e.target.value)}
                    />
                  </FormColumn>
                </FormRow>

                <ChipSection>
                  <ChipLabel>{t('storyGenerator.form.includeMagic.label')}</ChipLabel>
                  <ChipGrid>
                    <Chip
                      $selected={config.includeMagic === true}
                      onClick={() => handleConfigChange('includeMagic', true)}
                    >
                      {t('storyGenerator.form.includeMagic.yes')}
                    </Chip>
                    <Chip
                      $selected={config.includeMagic === false}
                      onClick={() => handleConfigChange('includeMagic', false)}
                    >
                      {t('storyGenerator.form.includeMagic.no')}
                    </Chip>
                  </ChipGrid>
                </ChipSection>

                {/* ── Supporting characters (collapsible) ──── */}
                <FormSection>
                  <CollapsibleHeader
                    $open={showSupportingChars}
                    onClick={() => setShowSupportingChars(v => !v)}
                  >
                    <span>{t('storyGenerator.sections.supportingCharacters')}</span>
                    <ChevronDown />
                  </CollapsibleHeader>

                  <CollapsibleBody $open={showSupportingChars}>
                  <ChipSection>
                    {config.supportingCharacters.map((character) => (
                      <CharacterCard key={character.id}>
                        <CharacterHeader>
                          <CharacterTitle>{character.name} ({characterTypeOptions.find(opt => opt.value === character.type)?.label || character.type})</CharacterTitle>
                          <Button
                            variant="danger"
                            size="small"
                            onClick={() => handleRemoveSupportingCharacter(character.id)}
                          >
                            {t('storyGenerator.buttons.remove')}
                          </Button>
                        </CharacterHeader>
                        <TraitContainer>
                          {character.traits.map((trait, index) => (
                            <Trait key={index}>{trait}</Trait>
                          ))}
                        </TraitContainer>
                      </CharacterCard>
                    ))}
                    
                    <FormRow>
                      <FormColumn>
                        <CustomInput
                          label={supportingCharType === CharacterType.OBJECT
                            ? t('storyGenerator.form.objectName.label', 'Object name')
                            : t('storyGenerator.form.characterName.label')}
                          placeholder={supportingCharType === CharacterType.OBJECT
                            ? t('storyGenerator.form.objectName.placeholder', 'e.g. a magical oak tree, a talking flower')
                            : t('storyGenerator.form.characterName.placeholder')}
                          value={supportingCharName}
                          onChange={(e) => setSupportingCharName(e.target.value)}
                        />
                      </FormColumn>
                    </FormRow>

                    <ChipLabel>{t('storyGenerator.form.characterType.label')}</ChipLabel>
                    <ChipGrid>
                      {characterTypeOptions.map(opt => {
                        const Icon = CHARACTER_TYPE_ICONS[opt.value];
                        return (
                          <Chip
                            key={opt.value}
                            $selected={supportingCharType === opt.value}
                            onClick={() => handleSupportingCharTypeChange(opt.value as CharacterType)}
                          >
                            {Icon && <Icon />}
                            {opt.label}
                          </Chip>
                        );
                      })}
                    </ChipGrid>
                    
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 500, color: '#343a40' }}>
                      {t('storyGenerator.form.characterTraits.label')}
                    </label>
                    
                    <InfoMessage>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 16v-4"/>
                        <path d="M12 8h.01"/>
                      </svg>
                      <span>{t('storyGenerator.form.characterTraits.infoMessage')}</span>
                    </InfoMessage>

                    <TraitChipRows
                      rows={buildTraitRows(supportingCharType)}
                      selected={supportingCharTraits}
                      onToggle={toggleSupportingCharTrait}
                    />

                    <TraitContainer>
                      {supportingCharTraits.map((trait, index) => (
                        <Trait key={index}>
                          {trait}
                          <button onClick={() => handleRemoveSupportingCharTrait(index)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </Trait>
                      ))}
                    </TraitContainer>
                    
                    <TraitInput>
                          <ResizableTextarea
                            placeholder={t('storyGenerator.form.characterTraits.supportingPlaceholder')}
                            value={supportingCharTrait}
                            onChange={(e) => setSupportingCharTrait(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddSupportingCharTrait();
                              }
                            }}
                            rows={2}
                          />
                      <Button
                        variant="secondary"
                        onClick={handleAddSupportingCharTrait}
                        disabled={!supportingCharTrait.trim()}
                      >
                        {t('storyGenerator.buttons.add')}
                      </Button>
                    </TraitInput>
                    </ChipSection>
                    
                    <AddButton
                      variant="outline"
                      onClick={handleAddSupportingCharacter}
                      disabled={!supportingCharName.trim()}
                      style={{ marginTop: '16px' }}
                    >
                      {t('storyGenerator.buttons.addSupportingCharacter')}
                    </AddButton>
                  </CollapsibleBody>
                </FormSection>

                {/* ── Additional details (collapsible) ─────── */}
                <FormSection>
                  <CollapsibleHeader
                    $open={showAdditionalDetails}
                    onClick={() => setShowAdditionalDetails(v => !v)}
                  >
                    <span>{t('storyGenerator.sections.additionalDetails')}</span>
                    <ChevronDown />
                  </CollapsibleHeader>

                  <CollapsibleBody $open={showAdditionalDetails}>
                    <FormRow>
                      <FormColumn>
                        <CustomInput
                          label={t('storyGenerator.form.moral.label')}
                          placeholder={t('storyGenerator.form.moral.label')}
                          value={config.moral || ''}
                          onChange={(e) => handleConfigChange('moral', e.target.value)}
                        />
                      </FormColumn>
                    </FormRow>

                    <FormRow>
                      <FormColumn>
                        <CustomInput
                          label={t('storyGenerator.form.educationalFocus.label')}
                          placeholder={t('storyGenerator.form.educationalFocus.placeholder')}
                          value={config.educationalFocus || ''}
                          onChange={(e) => handleConfigChange('educationalFocus', e.target.value)}
                        />
                      </FormColumn>
                    </FormRow>

                  </CollapsibleBody>
                </FormSection>

                {/* ── Generate images toggle (always visible) ─ */}
                <ImagesPanel>
                  <ChipLabel style={{ fontWeight: 600, color: COLORS.PRIMARY, textAlign: 'center' }}>
                    {t('storyGenerator.form.generateImages.label')}
                  </ChipLabel>
                  <CenteredChipGrid>
                    <Chip
                      $selected={config.generateImages === true}
                      onClick={() => handleConfigChange('generateImages', true)}
                    >
                      {t('storyGenerator.form.generateImages.yes')}
                    </Chip>
                    <Chip
                      $selected={config.generateImages === false}
                      onClick={() => handleConfigChange('generateImages', false)}
                    >
                      {t('storyGenerator.form.generateImages.no')}
                    </Chip>
                  </CenteredChipGrid>
                </ImagesPanel>

                {/* validation errors */}
                {!config.mainCharacter.name.trim() && (
                      <div style={{
                        color: '#d93025',
                        fontSize: '13px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(217, 48, 37, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {t('storyGenerator.validationErrors.mainCharacterName', 'Please add main character\'s name')}
                      </div>
                    )}

                {/* ── Generate button ── */}
                <NavRow>
                  <Button variant="outline" onClick={() => setStep(1)}>
                    ← {t('storyGenerator.buttons.back', 'Back')}
                  </Button>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleGenerateStory}
                      disabled={isGenerateDisabled}
                      isLoading={loading}
                    >
                      {t('storyGenerator.buttons.generateStory')}
                      <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                        ({creditCost} {t('storyGenerator.buttons.credits')})
                      </span>
                    </Button>

                    {/* {!config.mainCharacter.name.trim() && (
                      <div style={{
                        color: '#d93025',
                        fontSize: '13px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(217, 48, 37, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {t('storyGenerator.validationErrors.mainCharacterName', 'Please add main character\'s name')}
                      </div>
                    )} */}
                  </div>
                </NavRow>
              </StepContent>
            )}
          </>
        )}
      </GeneratorContainer>

      {/* Credit-related Modals */}
      <CreditConfirmation
        isOpen={showCreditConfirmation}
        currentCredits={credits}
        onConfirm={handleConfirmedGenerate}
        onCancel={() => {
          setShowCreditConfirmation(false);
          setGenerationError(null);
          setFinalConfigForGeneration(null);
        }}
        selectedLanguage={config.language}
        error={generationError || undefined}
        isLoading={isGeneratingStory}
        creditCost={creditCost}
      />
      
      <InsufficientCreditsModal
        isOpen={showInsufficientCredits}
        currentCredits={credits}
        onPurchase={handlePurchaseClick}
        onClose={() => setShowInsufficientCredits(false)}
      />
      
      <PurchaseModal
        isOpen={showPurchaseModal}
        currentCredits={credits}
        onClose={() => {
          setShowPurchaseModal(false);
          refreshCredits();
        }}
      />
    </>
  );
};